import KAllTrucksMapDialog from "kinnect-front/KYard/TrucksMap/dialogs/KAllTrucksMapDialog";
import KSchedulesTable from "kinnect-front/KYard/Schedule/KSchedulesTable";
import KTripHistoryMapDialog from "kinnect-front/KYard/TrucksMap/dialogs/KTripHistoryMapDialog";
import { Crudable, ToolbarActionable } from "kinnect-mixins";
import {
	KActionDialog,
	KDataTable,
	KDataTableMenu,
	KLink,
	KTimeAgo
} from "kinnect-general";
import {
	VBtn,
	VCard,
	VCardText,
	VCol,
	VContainer,
	VIcon,
	VRow,
	VStepper,
	VStepperContent,
	VStepperStep,
	VSubheader
} from "vuetify/lib";
import {
	formatCurrency,
	formatDate,
	formatTime
} from "kinnect-formatters";

export const defaultSchedulesTableProps = {
	dense: true,
	hideDefaultFooter: true,
	hiddenCols: [
		"actions",
		"age",
		"deliveryDate",
		"deliveryTime",
		"estLoadTime"
	],
	itemsPerPage: -1,
	mobileBreakpoint: 750
};

export default KDataTable.extend( {
	name: "KTripsTable",

	mixins: [
		Crudable,
		ToolbarActionable
	],

	props: {
		linkAddress: {
			default: true,
			type: Boolean
		},

		mobileBreakpoint: {
			...KDataTable.options.props.mobileBreakpoint,
			default: 750
		},

		noDataText: {
			...KDataTable.options.props.noDataText,
			default: "There are no trips to display"
		},

		noResultsText: {
			...KDataTable.options.props.noResultsText,
			default: "No matching trips found"
		},

		schedulesTableProps: {
			default: () => defaultSchedulesTableProps,
			type: Object
		},

		sortBy: {
			...KDataTable.options.props.sortBy,
			default: "driverNames"
		}
	},

	data: vm => ( {
		crud: "yard_trips",
		expandedItems: vm.items,
		truckStoreModuleName: "all-trucks-map",
		tripStepNum: {
			"Assigned": 1,
			"Loading": 2,
			"On Route": 3,
			"Completed": 4
		},
		dialogs: {
			trucksMap: {
				idTruck: null,
				open: false
			}
		},
		toolbarActions: {
			icon: "local_shipping",
			onClick: vm.openTrucksMap,
			text: "Trucks Map",
			tooltip: "View trucks on map"
		}
	} ),

	computed: {
		$_schedulesTableProps() {
			return {
				...defaultSchedulesTableProps,
				...this.schedulesTableProps
			};
		},

		truckStoreModuleRegistered() {
			return this.$store.state.hasOwnProperty( this.truckStoreModuleName );
		},

		defaultHeaders() {
			return [
				{
					divider: true,
					value: "data-table-expand",
					width: "1px"
				},
				{
					text: "Driver(s)",
					value: "driverNames"
				},
				{
					text: "Status",
					value: "$.status"
				},
				{
					text: "Cost",
					value: "cost"
				},
				this.canEdit && {
					text: "Errors",
					value: "errors"
				},
				{
					text: "Stops",
					value: "schedules.length"
				},
				{
					text: "Truck",
					value: "truck.name"
				},
				{
					text: "Date",
					value: "deliveryDate"
				},
				{
					text: "ETD",
					value: "estDepartureTime"
				},
				{
					text: "ETR",
					value: "estReturnTime"
				},
				{
					align: "end",
					sortable: false,
					text: this.isMobile ? "Actions" : "",
					value: "actions"
				}
			];
		},

		canReadYardTripsHistory() {
			return this.$can( "read_yard_trips_history" );
		}
	},

	methods: {
		openTrucksMap( truck = null ) {
			const markerData = truck
				? {
					id: truck.id,
					type: "truck"
				}
				: null;
			const updateStore = () => {
				this.$store.dispatch(
					`${this.truckStoreModuleName}/setRootActiveMarker`,
					markerData
				);

				this.$store.dispatch(
					`${this.truckStoreModuleName}/setAutoFollow`,
					Boolean( truck )
				);
			};

			if ( this.truckStoreModuleRegistered ) {
				updateStore();
			} else {
				const unwatch = this.$watch( "truckStoreModuleRegistered", registered => {
					if ( registered ) {
						updateStore();
						unwatch();
					}
				} );
			}

			this.dialogs.trucksMap.open = true;
		},

		updateTripStatus( trip, status ) {
			trip.status = status;

			trip.save()
			.then( updatedTrip => {
				this.$emit( "tripUpdated", updatedTrip );

				if ( status === "completed" ) {
					this.$emit( "tripCompleted", updatedTrip );
				}
			} );
		},

		deleteTrip( trip ) {
			const deleteIndex = this.items.findIndex( item => trip.id === item.id );

			return trip.delete().then( () => {
				this.$delete( this.items, deleteIndex );

				this.$emit( "tripRemoved", trip );
			} );
		}
	},

	watch: {
		"items.length"( val, oldVal ) {
			if ( val !== oldVal ) {
				this.expandedItems = this.items;
			}
		}
	},

	render() {
		return <KDataTable
			attrs={{
				...this.$props,
				expanded: this.expandedItems
			}}
			expandFlat
			headers={this.$_headers}
			on={{ "update:expanded": val => this.expandedItems = val }}
			showExpand
			scopedSlots={{
				"item.$.status": ( { item } ) => [
					this.linkAddress && item.$.status === "On Route"
						? <KLink
							icon="local_shipping"
							nativeOnClick={() => this.openTrucksMap( item.truck )}
						>{item.$.status}</KLink>
						: item.$.status,

					item.$.status === "Loading" && <KTimeAgo
						startTime={new Date( `${item.deliveryDate}T${item.loadingTime}` ).getTime()}
						tag="div"
					></KTimeAgo>
				],

				"item.deliveryDate": ( { item } ) => formatDate( item.deliveryDate, "MMM d" ),

				"item.cost": ( { item } ) => formatCurrency( item.cost ),

				"item.errors": ( { item } ) => item.errors.length + item.purchaseOrderErrors.length + item.salesOrderErrors.length,

				"item.estDepartureTime": ( { item } ) =>
					<span class="text-no-wrap">
						{formatTime( item.estDepartureTime )}
					</span>,

				"item.estReturnTime": ( { item } ) =>
					<span class="text-no-wrap">
						{formatTime( item.estReturnTime )}
					</span>,

				"item.actions": ( { item } ) =>
					<KDataTableMenu closeOnContentClick={false}>
						<VCard dark>
							<VContainer fluid>
								<VRow>
									{this.canEdit && <VCol>
										<VStepper value={this.tripStepNum[item.$.status]} vertical>
											<VSubheader>Trip Status</VSubheader>

											<VStepperStep
												step="1"
												complete={this.tripStepNum[item.$.status] > 1}
											>
												<span>Assigned</span>
												<span class="text-caption text-no-wrap">
													({formatTime( item.created_at )})
												</span>
											</VStepperStep>

											<VStepperContent step="1">
												<VBtn
													color="primary"
													depressed
													loading={this.$wait.is( "save trip" )}
													onClick={() => this.updateTripStatus( item, "loading" )}
												>Mark as Loading</VBtn>
											</VStepperContent>

											<VStepperStep
												step="2"
												complete={this.tripStepNum[item.$.status] > 2}
											>
												<span>Loading</span>
												{item.loadingTime && <span class="text-caption text-no-wrap">
													({formatTime( item.loadingTime )})
												</span>}
											</VStepperStep>

											<VStepperContent step="2">
												<VBtn
													color="primary"
													depressed
													loading={this.$wait.is( "save trip" )}
													onClick={() => this.updateTripStatus( item, "onroute" )}
												>Mark as On Route</VBtn>
											</VStepperContent>

											<VStepperStep
												step="3"
												complete={this.tripStepNum[item.$.status] > 3}
											>
												<span>On Route</span>
												{item.onrouteTime && <span class="text-caption text-no-wrap">
													({formatTime( item.onrouteTime )})
												</span>}
											</VStepperStep>

											<VStepperContent step="3">
												<VBtn
													color="primary"
													depressed
													loading={this.$wait.is( "save trip" )}
													onClick={() => this.updateTripStatus( item, "completed" )}
												>Mark as Completed</VBtn>
											</VStepperContent>

											<VStepperStep step="4">
												<span>Completed</span>
												{item.completeTime && <span class="text-caption text-no-wrap">
													({formatTime( item.completeTime )})
												</span>}
											</VStepperStep>
										</VStepper>
									</VCol>}

									<VCol>
										<VBtn
											block
											class="btn--text-left mb-2"
											depressed
											large
											to={{
												name: "yardTrip",
												params: { idTrip: item.id }
											}}
										><VIcon left>visibility</VIcon> View</VBtn>

										<VBtn
											block
											class="btn--text-left mb-2"
											depressed
											large
											to={{
												name: "yardTripDeliveryNote",
												params: { idTrip: item.id }
											}}
										>
											<VIcon left>assignment_turned_in</VIcon>
											Delivery Note
										</VBtn>

										{this.canDelete
											&& !item.completed
											&& <KActionDialog
												confirmButtonColor="error"
												confirmButtonText="Delete"
												loading={this.$wait.is( "delete trip" )}
												onConfirm={() => this.deleteTrip( item )}
												scopedSlots={{
													activator: ( { on } ) =>
														<VBtn
															block
															class="btn--text-left mb-2"
															depressed
															large
															on={on}
														><VIcon left>delete</VIcon> Delete</VBtn>
												}}
												title="Delete Trip"
											>
												<span>Are you sure you want to delete the trip for <strong>{item.driverNames}</strong>?</span>
											</KActionDialog>
										}

										{this.canReadYardTripsHistory
											&& item.completed
											&& <KTripHistoryMapDialog
												idTrip={item.id}
												scopedSlots={{
													activator: ( { on } ) =>
														<VBtn
															block
															class="btn--text-left"
															depressed
															large
															on={on}
														>
															<VIcon left>history</VIcon>
															Route Replay
														</VBtn>
												}}
											></KTripHistoryMapDialog>
										}
									</VCol>
								</VRow>
							</VContainer>
						</VCard>
					</KDataTableMenu>,

				"expanded-item": ( { headers, item } ) =>
					<td colspan={headers.length}>
						<KSchedulesTable
							attrs={this.$_schedulesTableProps}
							items={item.schedules}
							scopedSlots={{
								top: () => <VCardText class="px-2 py-1">Stops</VCardText>
							}}
						></KSchedulesTable>
					</td>,

				"footer": () => [
					<KAllTrucksMapDialog
						storeModuleName={this.truckStoreModuleName}
						vModel={this.dialogs.trucksMap.open}
					></KAllTrucksMapDialog>
				],

				...this.$scopedSlots
			}}
		></KDataTable>;
	}
} );
